import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';
import { formatDate } from '../../utils/formatDate';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/admin/dashboard');
        setStats(data);
      } catch (err) {
        console.error('Admin dashboard error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      label: 'Organizations', value: stats?.totalOrgs, color: '#60a5fa',
      icon: <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>,
    },
    {
      label: 'Users', value: stats?.totalUsers, color: '#a855f7',
      icon: <><path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></>,
    },
    {
      label: 'Appliances', value: stats?.totalAppliances, color: '#06b6d4',
      icon: <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    },
    {
      label: 'Online', value: stats?.onlineCount, color: '#10b981',
      icon: <><path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><circle cx="12" cy="20" r="1"/></>,
    },
    {
      label: 'Open Tickets', value: stats?.openTickets, color: '#f59e0b',
      icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    },
    {
      label: 'Pending Payments', value: stats?.pendingPayments, color: '#ef4444',
      icon: <><path d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></>,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] text-[#475569] mb-[2px]">Admin</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Admin Dashboard</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">System overview for Tritech Central</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-7">
        {statCards.map((s, i) => (
          <div key={i} className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-[16px_18px] relative overflow-hidden hover:border-[rgba(148,163,184,0.10)] transition-colors">
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: s.color }} />
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] text-[#475569] uppercase tracking-wider">{s.label}</p>
              <div className="w-7 h-7 rounded-lg bg-[rgba(148,163,184,0.04)] flex items-center justify-center">
                <svg className="w-4 h-4 opacity-40" style={{ color: s.color }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  {s.icon}
                </svg>
              </div>
            </div>
            <p className="text-[22px] font-bold font-mono" style={{ color: s.color }}>{s.value ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-7">
        <Link to="/admin/certificates/new">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] text-center py-6 hover:border-[rgba(148,163,184,0.10)] hover:bg-[#151d2e] transition-all cursor-pointer">
            <div className="w-12 h-12 bg-[rgba(6,182,212,0.1)] rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-[#06b6d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            </div>
            <p className="text-[13px] font-medium text-[#e2e8f0]">Issue Certificate</p>
            <p className="text-[11px] text-[#475569] mt-1">Generate license key & PDFs</p>
          </div>
        </Link>
        <Link to="/admin/certificates">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] text-center py-6 hover:border-[rgba(148,163,184,0.10)] hover:bg-[#151d2e] transition-all cursor-pointer">
            <div className="w-12 h-12 bg-[rgba(59,130,246,0.1)] rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-[#60a5fa]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </div>
            <p className="text-[13px] font-medium text-[#e2e8f0]">Manage Certificates</p>
            <p className="text-[11px] text-[#475569] mt-1">Ship, deliver, track</p>
          </div>
        </Link>
        <Link to="/admin/payments">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] text-center py-6 hover:border-[rgba(148,163,184,0.10)] hover:bg-[#151d2e] transition-all cursor-pointer">
            <div className="w-12 h-12 bg-[rgba(245,158,11,0.1)] rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-[#f59e0b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <p className="text-[13px] font-medium text-[#e2e8f0]">Confirm Payments</p>
            <p className="text-[11px] text-[#475569] mt-1">{stats?.pendingPayments || 0} pending</p>
          </div>
        </Link>
      </div>

      {/* Recent certificates */}
      {stats?.recentCerts?.length > 0 && (
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-[18px_20px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[14px] font-semibold text-[#e2e8f0]">Recent Certificates</h3>
            <Link to="/admin/certificates" className="text-[11.5px] text-[#06b6d4] hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {stats.recentCerts.map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-[rgba(148,163,184,0.06)] last:border-0">
                <div>
                  <p className="text-[13px] font-medium text-[#e2e8f0]">{c.softwareName}</p>
                  <p className="text-[11px] text-[#475569]">
                    {c.organization?.shortName || c.organization?.name} &middot; <span className="font-mono">{c.licenseKey}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-[#475569]">{formatDate(c.createdAt)}</span>
                  <StatusBadge status={c.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
